import {Trans, useTranslation} from 'react-i18next'
import {Anchor, Button} from '@translated/babele-react'
import {TAppLink} from '~/features/TLink'
import HubSpotForm from './HubSpotForm'
import {ActionFunctionArgs, Link, MetaFunction} from 'react-router'
import {CustomersLogos} from '~/ui/CustomersLogos'
import MailIllustration from '~/routes/sales/Illustrations/MailIllustration'
import PrizeCupIllustration from '~/routes/sales/Illustrations/PrizeCupIllustration'
import {enrichedMeta} from '~/utils/meta.server'
import {TranslatedLogo} from '../about/TranslatedLogo'

export const handle = {
  i18n: 'sales'
}

export const meta: MetaFunction<typeof loader> = ({data}) => {
  if (!data?.meta) return []

  return data.meta
}

export const loader = async ({request}: ActionFunctionArgs) => {
  return {
    meta: await enrichedMeta({
      with: ['og'],
      request,
      namespace: 'sales'
    })
  }
}

export {ErrorBoundary} from '~/features/RootErrorBoundary'

export default function Sales() {
  const {t, i18n} = useTranslation('sales')

  return (
    <main>
      <section className="mx-auto flex max-w-7xl flex-col gap-6 px-3 pb-10 pt-16 text-neutral-25 lg:flex-row lg:gap-[6rem] lg:px-4 lg:pt-[6rem]">
        <div className="flex flex-col gap-12 text-center lg:text-left">
          <h1 className="text-[2rem] font-bold leading-none lg:max-w-[18ch] lg:text-[4rem]">
            {t('hero_section.title')}
          </h1>
          <CustomersLogos className="justify-center lg:max-w-[30rem] lg:justify-start" />
          <p className="text-pretty translated-light:text-light-violet-400 lg:text-lg">
            {t('hero_section.description')}
          </p>
        </div>
        <div className="mx-auto w-full rounded-sm bg-neutral-25 text-neutral-1000 md:min-h-[46rem] md:w-[47rem]">
          <HubSpotForm language={i18n.language} />
        </div>
      </section>
      <section className="w-full rounded-t-lg bg-light-utility-violet-1 px-4">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 py-16 lg:gap-12 lg:pb-[5rem] lg:pt-[6rem]">
          <div className="flex flex-col items-center gap-[6rem]">
            <h2 className="max-w-[22ch] text-center text-display-md font-bold lg:text-display-lg">
              {t('benefits_section.title')}
            </h2>
            <div className="flex flex-col gap-8 md:flex-row">
              <div className="flex flex-col justify-between gap-6 rounded-lg bg-neutral-25 p-6 md:w-1/2">
                <PrizeCupIllustration className="size-10" />
                <h3 className="text-display-md font-medium">
                  {t('benefits_section.card_1.title')}
                </h3>
                <ul className="flex list-disc flex-col gap-3 pl-8 text-lg text-neutral-900">
                  <li>
                    <Trans
                      i18nKey="benefits_section.card_1.list.0"
                      t={t}
                      components={{b: <b className="font-medium text-neutral-1000" />}}
                    />
                  </li>
                  <li>
                    <Trans
                      i18nKey="benefits_section.card_1.list.1"
                      t={t}
                      components={{b: <b className="font-medium text-neutral-1000" />}}
                    />
                  </li>
                  <li>
                    <Trans
                      i18nKey="benefits_section.card_1.list.2"
                      t={t}
                      components={{b: <b className="font-medium text-neutral-1000" />}}
                    />
                  </li>
                </ul>
                <Button
                  render={<TAppLink to="/translate">{t('benefits_section.card_1.cta')}</TAppLink>}
                  className="w-fit"
                  variant={{tone: 'accent', size: 'lg'}}
                />
              </div>
              <div className="flex flex-col justify-between gap-6 rounded-lg bg-neutral-25 p-6 md:w-1/2">
                <MailIllustration className="size-10" />
                <h3 className="text-display-md font-medium">
                  {t('benefits_section.card_2.title')}
                </h3>
                <ul className="flex flex-col gap-3 text-lg text-neutral-900">
                  <li>
                    <Trans
                      i18nKey="benefits_section.card_2.list.0"
                      t={t}
                      components={{
                        b: <b className="font-medium text-neutral-1000" />,
                        a: <Anchor href="mailto:support@laratranslate.com" />
                      }}
                    />
                  </li>
                  <li>
                    <Trans
                      i18nKey="benefits_section.card_2.list.1"
                      t={t}
                      components={{
                        b: <b className="font-medium text-neutral-1000" />,
                        a: <Anchor render={<TAppLink to="/pricing" />} />
                      }}
                    />
                  </li>
                </ul>
                <div className="mt-auto flex flex-col flex-wrap items-center gap-2 lg:flex-row lg:gap-5">
                  <Button
                    render={
                      <Link to="mailto:support@laratranslate.com">
                        {t('benefits_section.card_2.cta_1')}
                      </Link>
                    }
                    className="w-full lg:w-fit"
                    variant={{size: 'lg'}}
                  />
                  <span className="text-neutral-900">{t('benefits_section.card_2.or')}</span>
                  <Button
                    onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
                    className="w-full lg:w-fit"
                    variant={{tone: 'accent', size: 'lg'}}
                  >
                    {t('benefits_section.card_2.cta_2')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-light-violet-100 px-4">
        <div className="mx-auto flex max-w-[60rem] flex-col items-center gap-16 py-16 text-center lg:gap-[6rem] lg:py-[6rem]">
          <p>{t('footer_section.language_importance')}</p>
          <p className="text-display-sm font-medium lg:text-display-md">
            {t('footer_section.understand_and_be_understood')}
          </p>
          <Anchor href="https://translated.com" target="_blank">
            <div className="scale-75 lg:scale-100">
              <TranslatedLogo />
            </div>
          </Anchor>
        </div>
      </section>
    </main>
  )
}
